/**
 * Phase 126.B — Classroom Support Hub: Giftedness reference page.
 *
 * Public, no-login, shareable URL. Strengths-first; classroom strategy,
 * not identification. The spec is explicit: no IQ talk, no labels —
 * just what shows up in your room and what helps.
 */
import ProfilePageLayout from "../../components/classroom-support/ProfilePageLayout";

export default function Giftedness() {
  return (
    <ProfilePageLayout
      slug="giftedness"
      metaTitle="Giftedness in the Classroom — Atomic Edu"
      metaDescription="What it looks like, the strengths it brings, and what helps. A teacher-to-teacher guide for supporting gifted students — depth over volume, not more of the same work."
      hero={{
        title: "Giftedness in the Classroom",
        tagline:
          "What it really looks like — beyond 'they finish first'. The strengths gifted students bring, the friction they hit when the work doesn't stretch, and the moves that build depth instead of volume.",
      }}
      sections={[
        {
          id: "looks_like",
          body:
            "It rarely looks the way the staff room jokes suggest. You might notice:",
          bullets: [
            "Rapid learning — picking up the new skill before you've finished the modelled example.",
            "Intense curiosity that overflows the lesson — questions that go three layers deeper than you planned for.",
            "Strong memory for the small details others let go.",
            "Perfectionism that looks like reluctance to start, or to hand in.",
            "Heightened sensitivity — to fairness, to feedback, to the social temperature of the room.",
            "Asynchrony — advanced thinking living alongside age-typical emotion.",
            "Boredom, doodling, or quiet underachievement when the task doesn't stretch.",
          ],
        },
        {
          id: "affects",
          body:
            "What gets in the way of these students growing across a year:",
          bullets: [
            "Repetition burns them out fast — practice for mastery feels like punishment.",
            "Fear of failure / perfectionism stalls the start of hard work.",
            "Uneven development — the cognitive runs ahead of the social or the executive.",
            "Mismatch with grade-level pacing — they're either waiting or coasting.",
          ],
        },
        {
          id: "strengths",
          body:
            "The reframe — what these students bring before anything else:",
          bullets: [
            "Advanced reasoning — they hold multiple variables, they spot the assumption.",
            "Creativity built on top of a wide knowledge base.",
            "Intrinsic motivation when the problem is real and the audience is real.",
            "Capacity for complexity — they don't need the task simplified, they need it opened up.",
            "A strong sense of justice and empathy that powers their best work.",
          ],
        },
        {
          id: "thrives",
          bullets: [
            "Depth over volume. The same expectation, examined further — not more questions.",
            "Open-ended challenge with no single right answer.",
            "Autonomy — choosing the angle, the format, the question.",
            "Intellectual peers — even one partner who thinks at the same speed.",
            "Real problems — a real audience, a real decision, a real consequence.",
          ],
        },
        {
          id: "strategies",
          body:
            "Curriculum compacting to free up time, higher-order questions that re-frame the same expectation, metacognition prompts, passion-project threads, productive-failure language for perfectionism, and real-world application. The generator below assembles these into a plan for the situation you type in.",
        },
      ]}
      ctas={undefined}
      generators={[
        {
          to: "/classroom-support/giftedness/generator",
          title: "Gifted extension plan",
          subtitle:
            "Type the situation (e.g., 'Grade 6, finishes everything in five minutes and is bored') and get an extension plan that adds depth, not extra worksheets.",
          cta: "Generate a plan",
        },
      ]}
      readNext={[
        {
          to: "/classroom-support/adhd",
          title: "ADHD in the Classroom",
          subtitle:
            "Movement, chunks, immediate feedback — what helps when the friction shows up.",
          cta: "Read the guide",
        },
        {
          to: "/classroom-support/2e",
          title: "Twice-Exceptional (2e)",
          subtitle:
            "When giftedness shows up alongside ADHD or another exceptionality, both can hide from teachers for years.",
          cta: "Read the guide",
        },
      ]}
    />
  );
}
